import { DestinationTypeMapping, ServiceRequirement } from "./definitions";

import { DateTime } from "luxon";

import _ from "lodash";

import { QuotationRequestDestination } from "./definitions";
import { QuotationAccommodation } from "./QuotationAccommodation";
import { QuotationCoordinator } from "./QuotationCoordinator";
import { QuotationActivity } from "./QuotationActivity";
import { QuotationTransfer } from "./QuotationTransfer";

function ServiceHeader({ label, count }: { label: string; count: number }) {
  return (
    <span>
      <strong>
        {label} ({count}):{" "}
      </strong>
    </span>
  );
}

type QuotationDestinationProps = {
  destination: QuotationRequestDestination;
  activities: ServiceRequirement[];
  coordinators: ServiceRequirement[];
  accommodation: ServiceRequirement[];
  transfers: ServiceRequirement[];
  excludedServiceUids: Set<string>;
  excludeSrv: (uid: string) => void;
  includeSrv: (uid: string) => void;
};

export function QuotationDestination({
  destination,
  activities,
  accommodation,
  transfers,
  coordinators,
  excludedServiceUids,
  excludeSrv,
  includeSrv,
}: QuotationDestinationProps) {
  return (
    <div className="p-3 shadow-md rounded-md grid gap-3 border border-gray-100">
      <div className="grid grid-cols-[1fr_4fr] gap-3 items-center">
        <img
          src={destination.images?.[0]?.hd}
          alt={destination.name_en}
          className="w-full object-cover rounded-md h-28"
        />
        <div className="grid gap-1 auto-rows-max">
          <h6>
            {destination.order}. {destination.name_en}
          </h6>
          <div className="grid gap-1">
            {destination.stay > 0 && (
              <span>
                <strong>Stay: </strong>
                {destination.stay} nights
              </span>
            )}
            <span>
              <strong>Dates: </strong>
              {destination.date_from !== destination.date_to
                ? `${DateTime.fromISO(destination.date_from).toLocaleString(
                    DateTime.DATE_FULL,
                  )} - ${DateTime.fromISO(destination.date_to).toLocaleString(DateTime.DATE_FULL)}`
                : DateTime.fromISO(destination.date_from).toLocaleString(DateTime.DATE_FULL)}
            </span>
            <span>
              <strong>Type: </strong>
              {DestinationTypeMapping[destination.dest_type]}
            </span>
            <span>
              <strong>Point Type: </strong>
              {_.capitalize(destination.point_type)}
            </span>
          </div>
        </div>
      </div>
      {destination.point_type === "destination" && (
        <div className="grid gap-2 items-center">
          <QuotationAccommodation
            accommodations={accommodation}
            excludedServiceUids={excludedServiceUids}
            excludeSrv={excludeSrv}
            includeSrv={includeSrv}
          />
          {destination.dates.map((date, idx) => {
            const activitiesForDay = activities.filter(
              (activity) => activity.service_date === date,
            );
            const coordinatorsForDay = coordinators.filter(
              (coordinator) => coordinator.service_date === date,
            );
            const transfersForDay = transfers.filter((transfer) => transfer.service_date === date);

            return (
              <div key={date} className="border border-gray-200 rounded-md p-1 px-2 grid gap-2">
                <span>
                  <strong>Day {destination.trip_index_days[idx]}: </strong>
                  {DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY)}
                </span>
                {coordinatorsForDay.length > 0 && (
                  <div className="grid gap-2 px-2 rounded-md p-1 grid-auto-rows-max border border-orange-300">
                    <ServiceHeader label="Coordinators" count={coordinatorsForDay.length} />
                    {coordinatorsForDay.map((coordinator, idx) => {
                      return (
                        <QuotationCoordinator
                          key={`${idx}-${coordinator.service_date}`}
                          coordinator={coordinator}
                          index={idx}
                          excludedServiceUids={excludedServiceUids}
                          excludeSrv={excludeSrv}
                          includeSrv={includeSrv}
                        />
                      );
                    })}
                  </div>
                )}
                {activitiesForDay.length > 0 && (
                  <div className="grid gap-2 px-2 rounded-md p-1 auto-rows-max border border-green-300">
                    <ServiceHeader label="Activities" count={activitiesForDay.length} />
                    {activitiesForDay.map((activity, idx) => (
                      <QuotationActivity
                        key={`${idx}-${activity.service_date}`}
                        activity={activity}
                        index={idx}
                        excludedServiceUids={excludedServiceUids}
                        excludeSrv={excludeSrv}
                        includeSrv={includeSrv}
                      />
                    ))}
                  </div>
                )}
                {transfersForDay.length > 0 && (
                  <div className="grid gap-1 px-2 rounded-md p-1 border border-blue-300">
                    <ServiceHeader label="Transfers" count={transfersForDay.length} />
                    {transfersForDay.map((transfer, idx) => (
                      <QuotationTransfer
                        key={`${idx}-${transfer.service_date}`}
                        transfer={transfer}
                        index={idx}
                        excludedServiceUids={excludedServiceUids}
                        excludeSrv={excludeSrv}
                        includeSrv={includeSrv}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
